<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentBotModal from './components/AgentBotModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');

const selectedBot = ref({});
const loading = ref({});
const modalType = ref(MODAL_TYPES.CREATE);
const agentBotModalRef = ref(null);
const agentBotDeleteDialogRef = ref(null);
const searchQuery = ref('');

// Filtered bots based on search
const filteredBots = computed(() => {
  if (!searchQuery.value) return agentBots.value;
  const query = searchQuery.value.toLowerCase();
  return agentBots.value.filter(
    bot =>
      bot.name.toLowerCase().includes(query) ||
      (bot.description && bot.description.toLowerCase().includes(query))
  );
});

const selectedBotName = computed(() => selectedBot.value?.name || '');

const openAddModal = () => {
  modalType.value = MODAL_TYPES.CREATE;
  selectedBot.value = {};
  agentBotModalRef.value.dialogRef.open();
};

const openEditModal = bot => {
  modalType.value = MODAL_TYPES.EDIT;
  selectedBot.value = bot;
  agentBotModalRef.value.dialogRef.open();
};

const openDeletePopup = bot => {
  selectedBot.value = bot;
  agentBotDeleteDialogRef.value.open();
};

const deleteAgentBot = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AGENT_BOTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
    selectedBot.value = {};
  }
};

const confirmDeletion = () => {
  loading.value[selectedBot.value.id] = true;
  deleteAgentBot(selectedBot.value.id);
  agentBotDeleteDialogRef.value.close();
};

onMounted(() => {
  store.dispatch('agentBots/get');
});
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="settings-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ t('AGENT_BOTS.HEADER') }}
      </h1>
      <button class="primary-action-btn" @click="openAddModal">
        <span class="i-ph-plus-bold size-4" />
        {{ $t('AGENT_BOTS.ADD.TITLE') }}
      </button>
    </header>

    <!-- Description -->
    <div class="ws-desc-bar px-6 py-3">
      <p class="text-sm" style="color: #4b5563">
        {{ t('AGENT_BOTS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Search and Filters -->
    <div class="ws-desc-bar px-6 py-3">
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-xs">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
            style="color: #9ca3af"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
            placeholder="Search bots..."
          />
        </div>
        <div class="text-sm self-center" style="color: #6b7280">
          {{ filteredBots.length }} bots
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto">
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <woot-loading-state :message="t('AGENT_BOTS.LIST.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredBots.length"
        class="flex items-center justify-center py-20"
      >
        <p style="color: #6b7280">{{ t('AGENT_BOTS.LIST.404') }}</p>
      </div>

      <!-- Bots Table -->
      <table v-else class="w-full">
        <thead>
          <tr class="ws-thead-row">
            <th class="px-6 py-3">
              {{ t('AGENT_BOTS.LIST.TABLE_HEADER.DETAILS') }}
            </th>
            <th class="px-6 py-3">
              {{ t('AGENT_BOTS.LIST.TABLE_HEADER.URL') }}
            </th>
            <th class="px-6 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="ws-tbody">
          <tr v-for="bot in filteredBots" :key="bot.id" class="ws-tr">
            <!-- Details -->
            <td class="px-6 py-4">
              <div class="flex items-center gap-4">
                <Avatar
                  :name="bot.name"
                  :src="bot.thumbnail"
                  :size="40"
                  rounded-full
                />
                <div>
                  <span class="block font-medium text-n-slate-12">
                    {{ bot.name }}
                    <span
                      v-if="bot.system_bot"
                      class="text-xs text-n-slate-12 bg-n-blue-5 inline-block rounded-md py-0.5 px-1 ltr:ml-1 rtl:mr-1"
                    >
                      {{ $t('AGENT_BOTS.GLOBAL_BOT_BADGE') }}
                    </span>
                  </span>
                  <span class="text-sm text-n-slate-11">
                    {{ bot.description || '-' }}
                  </span>
                </div>
              </div>
            </td>

            <!-- URL -->
            <td class="px-6 py-4">
              <span class="text-sm text-n-slate-11">
                {{ bot.outgoing_url || bot.bot_config?.webhook_url || '-' }}
              </span>
            </td>

            <!-- Actions -->
            <td class="px-6 py-4">
              <div class="flex justify-end gap-2">
                <Button
                  v-if="!bot.system_bot"
                  v-tooltip.top="t('AGENT_BOTS.EDIT.BUTTON_TEXT')"
                  icon="i-ph-pencil-simple-bold"
                  slate
                  xs
                  faded
                  :is-loading="loading[bot.id]"
                  @click="openEditModal(bot)"
                />
                <Button
                  v-if="!bot.system_bot"
                  v-tooltip.top="t('AGENT_BOTS.DELETE.BUTTON_TEXT')"
                  icon="i-ph-trash-bold"
                  xs
                  ruby
                  faded
                  :is-loading="loading[bot.id]"
                  @click="openDeletePopup(bot)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modals -->
    <AgentBotModal
      ref="agentBotModalRef"
      :type="modalType"
      :selected-bot="selectedBot"
    />

    <Dialog
      ref="agentBotDeleteDialogRef"
      type="alert"
      surface="light"
      :title="t('AGENT_BOTS.DELETE.CONFIRM.TITLE')"
      :description="
        t('AGENT_BOTS.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="t('AGENT_BOTS.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('AGENT_BOTS.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}

.settings-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb !important;
}

.ws-toolbar,
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}

.ws-content {
  background: #ffffff;
}

.ws-search,
.search-input {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}

.ws-search:focus,
.search-input:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  background: #ffffff !important;
}

.ws-search::placeholder,
.search-input::placeholder {
  color: #9ca3af !important;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.primary-action-btn:hover {
  background: #1f2937;
}

.primary-action-btn:active {
  transform: scale(0.98);
}

.primary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ws-thead-row th {
  padding: 10px 24px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151 !important;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.ws-tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s ease;
}

.ws-tbody tr:hover {
  background: #fafafa;
}

.ws-tbody td {
  color: #111827 !important;
  padding: 14px 24px;
  font-size: 13px;
  vertical-align: middle;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}
</style>
